import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, of, forkJoin, BehaviorSubject } from 'rxjs';
import { map, catchError, tap, switchMap, filter, take } from 'rxjs/operators';

export interface ContentData {
  id: string;
  content: SafeHtml;
  loaded: boolean;
  error?: string;
}

interface BundledContent {
  id: string;
  content: string;
}

@Injectable({
  providedIn: 'root'
})
export class ContentLoaderService {
  private contentCache = new Map<string, ContentData>();
  private bundledTutorials: BundledContent[] = [];
  private bundledTooltips: BundledContent[] = [];
  private preloadSubject = new BehaviorSubject<boolean | null>(null);

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {}

  initializePreloading(): Observable<boolean> {
    // Check if already initialized
    if (this.preloadSubject.value !== null) {
      return of(this.preloadSubject.value);
    }

    return forkJoin({
      tutorials: this.http.get<BundledContent[]>('/data/tutorials-content.json'),
      tooltips: this.http.get<BundledContent[]>('/data/tooltips-content.json')
    }).pipe(
      tap(({ tutorials, tooltips }) => {
        this.bundledTutorials = tutorials;
        this.bundledTooltips = tooltips;
        this.preloadAll();
        this.preloadSubject.next(true);
      }),
      map(() => true),
      catchError(error => {
        console.error('Error loading bundled content:', error);
        this.preloadSubject.next(false);
        return of(false);
      })
    );
  }

  loadContent(contentId: string, contentType: 'tutorial' | 'tip', language = 'en'): Observable<ContentData> {
    // Wait for preloading to complete before serving content
    return this.preloadSubject.pipe(
      filter(preloadComplete => preloadComplete === true),
      take(1),
      switchMap(() => {
        const cacheKey = `${contentType}-${language}-${contentId}`;

        if (this.contentCache.has(cacheKey)) {
          return of(this.contentCache.get(cacheKey)!);
        }

        // Find content in bundled data
        const bundledData = contentType === 'tutorial' ? this.bundledTutorials : this.bundledTooltips;
        const bundledItem = bundledData.find(item => item.id === contentId);

        if (bundledItem) {
          const sanitizedHtml = this.sanitizeHtml(bundledItem.content);
          const contentData: ContentData = {
            id: contentId,
            content: sanitizedHtml,
            loaded: true
          };

          this.contentCache.set(cacheKey, contentData);
          return of(contentData);
        }

        // Content not found in bundle
        const errorData: ContentData = {
          id: contentId,
          content: this.sanitizer.bypassSecurityTrustHtml('<p>Content not found. Try <span class="keys"><span class="key">Ctrl</span><span class="keys-plus">+</span><span class="key">F5</span></span>.</p>'),
          loaded: false,
          error: 'Content not found in bundle'
        };
        this.contentCache.set(cacheKey, errorData);
        return of(errorData);
      })
    );
  }

  private preloadAll(): void {
    // Preload all tutorials from bundled data
    this.bundledTutorials.forEach(item => {
      const cacheKey = `tutorial-en-${item.id}`;
      if (!this.contentCache.has(cacheKey)) {
        const sanitizedHtml = this.sanitizeHtml(item.content);
        const contentData: ContentData = {
          id: item.id,
          content: sanitizedHtml,
          loaded: true
        };
        this.contentCache.set(cacheKey, contentData);
      }
    });

    // Preload all tooltips from bundled data
    this.bundledTooltips.forEach(item => {
      const cacheKey = `tooltip-en-${item.id}`;
      if (!this.contentCache.has(cacheKey)) {
        const sanitizedHtml = this.sanitizeHtml(item.content);
        const contentData: ContentData = {
          id: item.id,
          content: sanitizedHtml,
          loaded: true
        };
        this.contentCache.set(cacheKey, contentData);
      }
    });
  }

  private sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
